import { createAsyncThunk } from "@reduxjs/toolkit";
import { api } from "../../config/api";

// Get all available subscription plans
export const getAvailableSubscrptionPlans = createAsyncThunk(
  "subscription/getAvailableSubscrptionPlans",
  async (currency, { rejectWithValue }) => {
    try {
      const url = currency
        ? `/subscriptions/plans?currency=${currency}`
        : `/subscriptions/plans`;
      const response = await api.get(url);
      console.log("all available subscription fetched:", response.data);
      return response.data;
    } catch (err) {
      console.error("Error fetching all available subscription:", err);
      return rejectWithValue(
        err.response?.data?.message ||
          "Failed to fetch all available subscription",
      );
    }
  },
);

// Get user subscription summary
export const getCurrentUserSubscriptionSummary = createAsyncThunk(
  "subscriptions/summary",
  async () => {
    const response = await api.get("/subscriptions/summary");
    return response.data;
  },
);

// Get active subscription plans
export const getActiveSubscrptionPlans = createAsyncThunk(
  "subscription/getMySubscrptionPlans",
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get("/subscriptions/active");
      console.log("active subscription fetched:", response.data);
      return response.data;
    } catch (err) {
      console.error("Error fetching active subscription:", err);
      return rejectWithValue(
        err.response?.data?.message || "Failed to fetch active subscription",
      );
    }
  },
);

// Get user subscription notifications
export const getSubscriptionNotification = createAsyncThunk(
  "subscription/getSubscriptionNotification",
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get("/subscriptions/notifications");
      console.log("user subscription notfication fetched:", response.data);
      return response.data;
    } catch (err) {
      console.error("Error fetching subscription notification:", err);
      return rejectWithValue(
        err.response?.data?.message ||
          "Failed to fetch subscription notification",
      );
    }
  },
);

// Check if user already has active subscription for a plan category
export const checkExistingSubscription = createAsyncThunk(
  "subscription/checkExistingSubscription",
  async (planId, { rejectWithValue }) => {
    try {
      const response = await api.get(
        `/subscriptions/check-existing?planId=${planId}`,
      );
      console.log("Existing subscription check:", response.data);
      return response.data;
    } catch (err) {
      console.error("Error checking existing subscription:", err);
      return rejectWithValue(
        err.response?.data?.message || "Failed to check existing subscription",
      );
    }
  },
);

// Get plan details by ID
export const getPlanDetails = createAsyncThunk(
  "subscription/getPlanDetails",
  async (planData, { rejectWithValue }) => {
    try {
      const { planId, currency } =
        typeof planData === "string" ? { planId: planData } : planData;
      const url = currency
        ? `/subscriptions/plans/${planId}?currency=${currency}`
        : `/subscriptions/plans/${planId}`;

      const response = await api.get(url);
      console.log("Plan details fetched:", response.data);
      return response.data;
    } catch (err) {
      console.error("Error fetching plan details:", err);
      return rejectWithValue(
        err.response?.data?.message || "Failed to fetch plan details",
      );
    }
  },
);

// Subscribe to a plan
export const subscribeToPlan = createAsyncThunk(
  "subscription/subscribeToPlan",
  async (subscriptionData) => {
    const response = await api.post(
      "/subscriptions/subscribe",
      subscriptionData,
      { skipGlobalErrorHandling: true },
    );
    console.log("Subscription successful:", response.data);
    return response.data;
  },
);

// Subscribe to a plan
export const subscribeToAiWorkflowPlan = createAsyncThunk(
  "subscription/subscribeToPlan",
  async (subscriptionData) => {
    const response = await api.post(
      "ai-workflow/subscriptions/ai-plan-subscribe",
      subscriptionData,
      { skipGlobalErrorHandling: true },
    );
    console.log("Subscription successful:", response.data);
    return response.data;
  },
);

export const captureSubscriptionPaypalPayment = createAsyncThunk(
  "subscription/capturePaypal",
  async ({ subscriptionId }) => {
    const response = await api.post("/subscriptions/capture-paypal", {
      subscriptionId, // Send the PayPal 'token' as subscriptionId
    });
    return response.data;
  },
);

// Get subscription details by ID
export const getSubscriptionById = createAsyncThunk(
  "subscription/getSubscriptionById",
  async (data) => {
    const response = await api.get(`subscriptions/active/${data}`);
    return response.data;
  },
);

// Get subscription payment success details
export const getSubscriptionPaymentSuccessDetails = createAsyncThunk(
  "/subscription/getSubscriptionPaymentSuccessDetails",
  async ({ paymentId, subscriptionId, isTrial, upgrade }, { rejectWithValue }) => {
    try {
      // For trial activations, use 'trial' as paymentId and pass subscriptionId as query param
      // This matches the backend's SCENARIO 2 handling
      let url = isTrial
        ? `/subscriptions/payments/success/trial?subscriptionId=${subscriptionId}`
        : `/subscriptions/payments/success/${paymentId}`;

      if (upgrade) {
        url += url.includes("?") ? "&upgrade=true" : "?upgrade=true";
      }

      const response = await api.get(url);
      return response.data;
    } catch (err) {
      console.error(
        "Error fetching subscription payment success details:",
        err,
      );
      return rejectWithValue(
        err.response?.data?.message ||
          "Failed to fetch subscription payment success details",
      );
    }
  },
);

// Get subscription payment failure details
export const fetchSubscriptionPaymentFailureDetails = createAsyncThunk(
  "/subscription/fetchSubscriptionPaymentFailureDetails",
  async (paymentId, { rejectWithValue }) => {
    try {
      const response = await api.get(
        `/subscriptions/payments/failure/${paymentId}`,
      );
      return response.data;
    } catch (err) {
      console.error(
        "Failed to fetch subscription payment failure details:",
        err,
      );
      return rejectWithValue(
        err.response?.data?.message ||
          "Failed to fetch subscription payment failure details",
      );
    }
  },
);

// Calculate subscription upgrade cost
export const calculateSubscriptionUpgrade = createAsyncThunk(
  "subscription/calculateUpgrade",
  async ({ subscriptionId, newPlanId }, { rejectWithValue }) => {
    try {
      const response = await api.post("/subscriptions/upgrade/calculate", {
        subscriptionId,
        newPlanId,
      });
      console.log("Upgrade calculation:", response.data);
      return response.data;
    } catch (err) {
      console.error("Error calculating upgrade:", err);
      return rejectWithValue(
        err.response?.data?.message || "Failed to calculate upgrade",
      );
    }
  },
);

// Process subscription upgrade
export const upgradeSubscription = createAsyncThunk(
  "subscription/upgrade",
  async (
    { subscriptionId, newPlanId, totalUpgradeCost, paymentMethod },
    { rejectWithValue },
  ) => {
    try {
      const response = await api.post("/subscriptions/upgrade", {
        subscriptionId,
        newPlanId,
        totalUpgradeCost,
        paymentMethod,
      });
      console.log("Subscription upgraded successfully:", response.data);
      return response.data;
    } catch (err) {
      console.error("Error upgrading subscription:", err);
      return rejectWithValue(
        err.response?.data?.message || "Failed to upgrade subscription",
      );
    }
  },
);

// ========== CANCELLATION THUNKS ==========

// Calculate cancellation preview (no refunds)
export const calculateCancellation = createAsyncThunk(
  "subscription/calculateCancellation",
  async ({ subscriptionId }, { rejectWithValue }) => {
    try {
      const response = await api.post("/subscriptions/cancel/calculate", {
        subscriptionId,
      });
      console.log("Cancellation calculation:", response.data);
      return response.data;
    } catch (err) {
      console.error("Error calculating cancellation:", err);
      return rejectWithValue(
        err.response?.data?.message || "Failed to calculate cancellation",
      );
    }
  },
);

// Cancel subscription (no refund, cancel at period end)
export const cancelSubscription = createAsyncThunk(
  "subscription/cancel",
  async ({ subscriptionId, reason, feedback }, { rejectWithValue }) => {
    try {
      const response = await api.post("/subscriptions/cancel", {
        subscriptionId,
        cancelReason: reason,
        cancelReasonDetails: feedback,
      });
      console.log("Subscription cancelled successfully:", response.data);
      return response.data;
    } catch (err) {
      console.error("Error cancelling subscription:", err);
      return rejectWithValue(
        err.response?.data?.message || "Failed to cancel subscription",
      );
    }
  },
);

// Reactivate cancelled subscription
export const reactivateSubscription = createAsyncThunk(
  "subscription/reactivate",
  async ({ subscriptionId }, { rejectWithValue }) => {
    try {
      const response = await api.post("/subscriptions/reactivate", {
        subscriptionId,
      });
      console.log("Subscription reactivated successfully:", response.data);
      return response.data;
    } catch (err) {
      console.error("Error reactivating subscription:", err);
      return rejectWithValue(
        err.response?.data?.message || "Failed to reactivate subscription",
      );
    }
  },
);

// ========== DOWNGRADE THUNKS ==========

// Calculate downgrade preview
export const calculateDowngrade = createAsyncThunk(
  "subscription/calculateDowngrade",
  async ({ subscriptionId, newPlanId }, { rejectWithValue }) => {
    try {
      const response = await api.post("/subscriptions/downgrade/calculate", {
        subscriptionId,
        newPlanId,
      });
      console.log("Downgrade calculation:", response.data);
      return response.data;
    } catch (err) {
      console.error("Error calculating downgrade:", err);
      return rejectWithValue(
        err.response?.data?.message || "Failed to calculate downgrade",
      );
    }
  },
);

// Schedule downgrade (effective at period end)
export const scheduleDowngrade = createAsyncThunk(
  "subscription/scheduleDowngrade",
  async ({ subscriptionId, newPlanId }, { rejectWithValue }) => {
    try {
      const response = await api.post("/subscriptions/downgrade", {
        subscriptionId,
        newPlanId,
      });
      console.log("Downgrade scheduled successfully:", response.data);
      return response.data;
    } catch (err) {
      console.error("Error scheduling downgrade:", err);
      return rejectWithValue(
        err.response?.data?.message || "Failed to schedule downgrade",
      );
    }
  },
);

// Cancel scheduled downgrade
export const cancelScheduledDowngrade = createAsyncThunk(
  "subscription/cancelScheduledDowngrade",
  async ({ subscriptionId }, { rejectWithValue }) => {
    try {
      const response = await api.post("/subscriptions/downgrade/cancel", {
        subscriptionId,
      });
      console.log("Scheduled downgrade cancelled successfully:", response.data);
      return response.data;
    } catch (err) {
      console.error("Error cancelling scheduled downgrade:", err);
      return rejectWithValue(
        err.response?.data?.message || "Failed to cancel scheduled downgrade",
      );
    }
  },
);

// ====== PLAN CAPACITY THUNKS ======

export const calculateExtraCapacity = createAsyncThunk(
  "subscription/calculateExtraCapacity",
  async ({ subscriptionId, additionalProjects }) => {
    const response = await api.post("/subscriptions/capacity/calculate", {
      subscriptionId,
      additionalProjects,
    });
    console.log("Capacity calculation:", response.data);
    return response.data;
  },
);

export const addExtraCapacity = createAsyncThunk(
  "subscription/addCapacity",
  async ({ subscriptionId, additionalProjects, paymentMethod }) => {
    const response = await api.post(
      "/subscriptions/capacity/add",
      {
        subscriptionId,
        additionalProjects,
        paymentMethod,
      },
      { skipGlobalErrorHandling: true },
    );
    console.log("Capacity added successfully:", response.data);
    return response.data;
  },
);

export const removeExtraCapacity = createAsyncThunk(
  "subscriptions/removeExtraCapacity",
  async (data) => {
    const response = await api.post("/subscriptions/capacity/remove", data);
    return response.data;
  },
);

// ----- SUBSCRIPTION PROJECT Management THUNKS

// Add project to subscription
export const addProjectToSubscription = createAsyncThunk(
  "subscription/addProjectToSubscription",
  async (data) => {
    const response = await api.post(`subscriptions/projects/add`, data);
    return response.data;
  },
);

// Remove subscription project
export const removeSubscriptionProject = createAsyncThunk(
  "subscription/removeSubscriptionProject",
  async (projectId) => {
    const response = await api.delete(`/subscriptions/projects/${projectId}`);
    return response.data;
  },
);

// ===================== PROJECT TEAM MANAGEMENT THUNKS =====================

// Get available team members for project
export const getAvailableProjectTeamMembers = createAsyncThunk(
  "subscription/getAvailableProjectTeamMembers",
  async (projectId) => {
    const response = await api.get(
      `/subscriptions/projects/${projectId}/available-team-members`,
    );
    console.log("Available team members fetched:", response.data);
    return response.data;
  },
);

// Add team members to project
export const addTeamMembersToProject = createAsyncThunk(
  "subscription/addTeamMembersToProject",
  async ({ projectId, teamMembers }) => {
    const payload = { teamMembers };

    const response = await api.post(
      `/subscriptions/projects/${projectId}/team-members`,
      payload,
    );
    console.log("Team members added:", response.data);
    return response.data;
  },
);

// Remove team member from project
export const removeTeamMemberFromProject = createAsyncThunk(
  "subscription/removeTeamMemberFromProject",
  async ({ projectId, userId }) => {
    const response = await api.delete(
      `/subscriptions/projects/${projectId}/team-member/${userId}`,
    );
    console.log("Team member removed:", response.data);
    return response.data;
  },
);

// ===================== PROJECT FILES THUNKS =====================

// Upload files to project
export const uploadProjectFiles = createAsyncThunk(
  "subscription/uploadProjectFiles",
  async ({ projectId, files, note, category }) => {
    const response = await api.post(
      `/subscriptions/projects/${projectId}/files`,
      { files, note, category },
    );
    console.log("Files uploaded:", response.data);
    return response.data;
  },
);

// ========== COMMENTS AND REPLIES THUNKS ==========

/**
 * Add comment to subscription project
 */
export const addCommentsToSubscribedProject = createAsyncThunk(
  "subscription/addCommentsToSubscribedProject",
  async ({ projectId, content, attachments = [] }) => {
    const response = await api.post(`/subscriptions/projects/comments`, {
      projectId,
      content,
      attachments,
    });
    return response.data;
  },
);

/**
 * Add reply to comment (can be nested)
 */
export const addReplyToProjectComment = createAsyncThunk(
  "subscription/addReplyToProjectComment",
  async ({
    commentId,
    content,
    attachments = [],
    isNestedReply = false,
    parentReplyId = null,
  }) => {
    const response = await api.post(
      `/subscriptions/projects/comments/${commentId}/reply`,
      { content, attachments, isNestedReply, parentReplyId },
    );
    return response.data;
  },
);

// ========== TICKET MANAGEMENT THUNKS ==========

/**
 * Raise ticket for subscription project
 */
export const raiseTicketForSubscribedProject = createAsyncThunk(
  "subscription/raiseTicketForSubscribedProject",
  async ({ projectId, category, title, description, attachments = [] }) => {
    const response = await api.post(`/subscriptions/projects/tickets/raise`, {
      projectId,
      category,
      title,
      description,
      attachments,
    });
    return response.data;
  },
);

/**
 * Add comment to ticket (can be nested)
 */
export const addCommentToTicket = createAsyncThunk(
  "subscription/addCommentToTicket",
  async ({
    ticketId,
    content,
    attachments = [],
    isNestedReply = false,
    parentCommentId = null,
  }) => {
    const response = await api.post(
      `/subscriptions/projects/tickets/${ticketId}/comments`,
      { content, attachments, isNestedReply, parentCommentId },
    );
    return response.data;
  },
);

// ======== SUBSCRIPTION PAYMENTS

// Get user subscription payments
export const getUserSubscriptionPayments = createAsyncThunk(
  "subscriptions/getUserPayments",
  async (params) => {
    const response = await api.get("/subscriptions/payments", { params });
    return response.data;
  },
);

// Get user subscriptions list for filter
export const getUserSubscriptionsList = createAsyncThunk(
  "subscriptions/getUserSubscriptionsList",
  async () => {
    const response = await api.get("/subscriptions/my-subscriptions");
    return response.data;
  },
);

// Get subscription receipt data
export const getSubscriptionReceiptData = createAsyncThunk(
  "subscriptions/getReceiptData",
  async (paymentId) => {
    const response = await api.get(`/subscriptions/receipts/${paymentId}`);
    return response.data;
  },
);
